/**
 * Moralis API Service
 * Multi-chain blockchain data provider
 *
 * Features:
 * - Token balances with prices
 * - NFT data
 * - DeFi positions
 * - Transaction history
 *
 * Requires: VITE_MORALIS_API_KEY
 */

import axios from 'axios';
import { callCoinGeckoAPI } from './api';
import type {
  Chain,
  UnifiedToken,
  DeFiPosition,
  NFTItem
} from '../types/wallet.types';

const MORALIS_API_KEY = import.meta.env.VITE_MORALIS_API_KEY;
const isDev = import.meta.env.DEV;

// In development: Use Vite proxy (direct API calls)
// In production: Use Vercel serverless proxy (API key on server)
const USE_SERVERLESS_PROXY = !isDev;
const MORALIS_BASE_URL = isDev ? '/moralis-api/api/v2.2' : '';

// In production, Moralis is available via serverless proxy (key on server)
// In development, check for VITE_MORALIS_API_KEY
const IS_MORALIS_AVAILABLE = USE_SERVERLESS_PROXY || !!MORALIS_API_KEY;

// Chain mapping for Moralis
const MORALIS_CHAIN_IDS: Record<Chain, string> = {
  ethereum: 'eth',
  bsc: 'bsc',
  polygon: 'polygon',
  arbitrum: 'arbitrum',
  optimism: 'optimism',
  avalanche: 'avalanche',
  base: 'base',
  fantom: 'fantom',
  gnosis: 'gnosis'
};

// Moralis API response types
interface MoralisTokenResponse {
  token_address: string;
  name: string;
  symbol: string;
  logo?: string;
  thumbnail?: string;
  decimals: number;
  balance: string;
  possible_spam?: boolean;
  verified_contract?: boolean;
  usd_price?: number;
  usd_price_24hr_percent_change?: number;
  usd_value?: number;
}

interface MoralisNFTResponse {
  token_address: string;
  token_id: string;
  name?: string;
  description?: string;
  metadata?: string;
  normalized_metadata?: {
    name?: string;
    description?: string;
    image?: string;
  };
  media?: {
    media_collection?: {
      low?: { url: string };
      medium?: { url: string };
      high?: { url: string };
    };
  };
  collection_name?: string;
  floor_price?: number;
  floor_price_currency?: string;
  possible_spam?: boolean;
}

interface MoralisDeFiPosition {
  protocol_name: string;
  protocol_id: string;
  protocol_url?: string;
  protocol_logo?: string;
  position_label?: string;
  position: {
    label: string;
    tokens: MoralisTokenResponse[];
    address: string;
    balance_usd: number;
    total_unclaimed_usd_value?: number;
  };
}

/**
 * Check if Moralis API is configured
 * In production: Always true (uses serverless proxy with API key on server)
 * In development: Requires VITE_MORALIS_API_KEY
 */
export function isMoralisEnabled(): boolean {
  return IS_MORALIS_AVAILABLE;
}

/**
 * Create axios instance with Moralis headers (for development/Vite proxy)
 */
function createMoralisClient() {
  if (!MORALIS_API_KEY) {
    throw new Error('Moralis API key not configured. Set VITE_MORALIS_API_KEY');
  }

  return axios.create({
    baseURL: MORALIS_BASE_URL,
    headers: {
      'X-API-Key': MORALIS_API_KEY,
      'Content-Type': 'application/json'
    },
    timeout: 30000
  });
}

/**
 * Call Moralis API via Vercel serverless proxy (for production)
 */
async function callMoralisProxy(endpoint: string, params: Record<string, unknown> = {}): Promise<any> {
  const response = await axios.post('/api/moralis/proxy', {
    endpoint,
    params
  }, {
    timeout: 30000
  });
  return response.data;
}

/**
 * Convert Moralis chain ID to our Chain type
 */
function moralisChainToChain(moralisChain: string): Chain {
  const mapping: Record<string, Chain> = {
    eth: 'ethereum',
    '0x1': 'ethereum',
    bsc: 'bsc',
    '0x38': 'bsc',
    polygon: 'polygon',
    '0x89': 'polygon',
    arbitrum: 'arbitrum',
    '0xa4b1': 'arbitrum',
    optimism: 'optimism',
    '0xa': 'optimism',
    avalanche: 'avalanche',
    '0xa86a': 'avalanche',
    base: 'base',
    '0x2105': 'base',
    fantom: 'fantom',
    '0xfa': 'fantom',
    gnosis: 'gnosis',
    '0x64': 'gnosis'
  };
  return mapping[moralisChain] || 'ethereum';
}

/**
 * Convert Moralis token to UnifiedToken
 */
function convertMoralisToken(token: MoralisTokenResponse, chain: Chain): UnifiedToken {
  const balance = parseFloat(token.balance) / Math.pow(10, token.decimals);
  return {
    symbol: token.symbol,
    name: token.name,
    address: token.token_address,
    chain,
    balance,
    balanceUSD: token.usd_value || balance * (token.usd_price || 0),
    price: token.usd_price || 0,
    priceChange24h: token.usd_price_24hr_percent_change,
    logoUrl: token.logo || token.thumbnail,
    isVerified: token.verified_contract,
    source: ['moralis']
  };
}

/**
 * Get token balances for a wallet on a specific chain
 */
export async function getTokenBalances(
  address: string,
  chain: Chain = 'ethereum'
): Promise<UnifiedToken[]> {
  if (!isMoralisEnabled() && !USE_SERVERLESS_PROXY) {
    console.warn('⚠️ Moralis API not configured');
    return [];
  }

  const chainId = MORALIS_CHAIN_IDS[chain];

  try {
    let data: MoralisTokenResponse[];

    if (USE_SERVERLESS_PROXY) {
      data = await callMoralisProxy(`/${address}/erc20`, {
        chain: chainId,
        exclude_spam: true
      });
    } else {
      const client = createMoralisClient();
      const response = await client.get(`/${address}/erc20`, {
        params: {
          chain: chainId,
          exclude_spam: true
        }
      });
      data = response.data || [];
    }

    const tokens: MoralisTokenResponse[] = data || [];
    return tokens
      .filter(t => !t.possible_spam && parseFloat(t.balance) > 0)
      .map(t => convertMoralisToken(t, chain))
      .sort((a, b) => b.balanceUSD - a.balanceUSD);
  } catch (error) {
    console.error(`❌ Moralis getTokenBalances error for ${chain}:`, error);
    return [];
  }
}

// Native token configurations for each chain
const NATIVE_TOKENS: Record<Chain, { symbol: string; name: string; geckoId: string; logo: string }> = {
  ethereum: { symbol: 'ETH', name: 'Ethereum', geckoId: 'ethereum', logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' },
  polygon: { symbol: 'MATIC', name: 'Polygon', geckoId: 'matic-network', logo: 'https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png' },
  bsc: { symbol: 'BNB', name: 'BNB', geckoId: 'binancecoin', logo: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png' },
  arbitrum: { symbol: 'ETH', name: 'Ethereum (Arbitrum)', geckoId: 'ethereum', logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' },
  optimism: { symbol: 'ETH', name: 'Ethereum (Optimism)', geckoId: 'ethereum', logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' },
  base: { symbol: 'ETH', name: 'Ethereum (Base)', geckoId: 'ethereum', logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' },
  avalanche: { symbol: 'AVAX', name: 'Avalanche', geckoId: 'avalanche-2', logo: 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png' },
  fantom: { symbol: 'FTM', name: 'Fantom', geckoId: 'fantom', logo: 'https://assets.coingecko.com/coins/images/4001/small/Fantom_round.png' },
  gnosis: { symbol: 'xDAI', name: 'xDAI', geckoId: 'xdai', logo: 'https://assets.coingecko.com/coins/images/11062/small/Identity-Primary-DarkBG.png' }
};

/**
 * Fetch native token prices from CoinGecko (uses proxy in production)
 */
async function fetchNativePrices(): Promise<Map<string, number>> {
  const prices = new Map<string, number>();
  try {
    const geckoIds = [...new Set(Object.values(NATIVE_TOKENS).map(t => t.geckoId))];
    const data = await callCoinGeckoAPI('/simple/price', {
      ids: geckoIds.join(','),
      vs_currencies: 'usd'
    });
    Object.entries(data).forEach(([id, priceData]: [string, any]) => {
      prices.set(id, priceData.usd || 0);
    });
  } catch {
    // Fallback prices
    prices.set('ethereum', 2000);
    prices.set('binancecoin', 300);
    prices.set('matic-network', 0.5);
    prices.set('avalanche-2', 25);
  }
  return prices;
}

/**
 * Get token balances including native token for a specific chain
 */
async function getTokenBalancesWithNative(
  address: string,
  chain: Chain,
  nativePrices: Map<string, number>
): Promise<UnifiedToken[]> {
  const tokens: UnifiedToken[] = [];

  // Get native balance first
  try {
    const nativeBalance = await getNativeBalance(address, chain);
    if (nativeBalance.balance && parseFloat(nativeBalance.balance) > 0) {
      const nativeConfig = NATIVE_TOKENS[chain];
      const nativePrice = nativePrices.get(nativeConfig.geckoId) || 0;
      const balance = parseFloat(nativeBalance.balance) / 1e18;

      if (balance > 0.0001) {
        tokens.push({
          symbol: nativeConfig.symbol,
          name: nativeConfig.name,
          address: '0x0000000000000000000000000000000000000000',
          chain,
          balance,
          balanceUSD: balance * nativePrice,
          price: nativePrice,
          logoUrl: nativeConfig.logo,
          isVerified: true,
          source: ['moralis']
        });
      }
    }
  } catch (error) {
    console.warn(`⚠️ Moralis native balance failed for ${chain}:`, error);
  }

  // Get ERC20 tokens
  try {
    const erc20Tokens = await getTokenBalances(address, chain);
    tokens.push(...erc20Tokens);
  } catch (error) {
    console.warn(`⚠️ Moralis ERC20 tokens failed for ${chain}:`, error);
  }

  return tokens;
}

/**
 * Get token balances across all supported chains (including native tokens)
 */
export async function getAllTokenBalances(address: string): Promise<UnifiedToken[]> {
  if (!isMoralisEnabled() && !USE_SERVERLESS_PROXY) {
    return [];
  }

  // Fetch native prices first
  const nativePrices = await fetchNativePrices();

  const chains: Chain[] = ['ethereum', 'polygon', 'bsc', 'arbitrum', 'optimism', 'base', 'avalanche'];
  const results = await Promise.allSettled(
    chains.map(chain => getTokenBalancesWithNative(address, chain, nativePrices))
  );

  const allTokens: UnifiedToken[] = [];
  results.forEach(result => {
    if (result.status === 'fulfilled') {
      allTokens.push(...result.value);
    }
  });

  console.log(`✅ Moralis multi-chain: Found ${allTokens.length} tokens across ${chains.length} chains`);

  return allTokens.sort((a, b) => b.balanceUSD - a.balanceUSD);
}

/**
 * Get native balance for a wallet
 */
export async function getNativeBalance(
  address: string,
  chain: Chain = 'ethereum'
): Promise<{ balance: string; balanceUSD: number }> {
  if (!isMoralisEnabled() && !USE_SERVERLESS_PROXY) {
    return { balance: '0', balanceUSD: 0 };
  }

  const chainId = MORALIS_CHAIN_IDS[chain];

  try {
    let data: { balance: string; usd_value?: number };

    if (USE_SERVERLESS_PROXY) {
      data = await callMoralisProxy(`/${address}/balance`, { chain: chainId });
    } else {
      const client = createMoralisClient();
      const response = await client.get(`/${address}/balance`, {
        params: { chain: chainId }
      });
      data = response.data;
    }

    return {
      balance: data.balance,
      balanceUSD: data.usd_value || 0
    };
  } catch (error) {
    console.error(`❌ Moralis getNativeBalance error for ${chain}:`, error);
    return { balance: '0', balanceUSD: 0 };
  }
}

/**
 * Get NFTs for a wallet
 */
export async function getNFTs(
  address: string,
  chain: Chain = 'ethereum'
): Promise<NFTItem[]> {
  if (!isMoralisEnabled() && !USE_SERVERLESS_PROXY) {
    return [];
  }

  const chainId = MORALIS_CHAIN_IDS[chain];

  try {
    let data: { result?: MoralisNFTResponse[] };

    if (USE_SERVERLESS_PROXY) {
      data = await callMoralisProxy(`/${address}/nft`, {
        chain: chainId,
        exclude_spam: true,
        normalizeMetadata: true,
        media_items: true
      });
    } else {
      const client = createMoralisClient();
      const response = await client.get(`/${address}/nft`, {
        params: {
          chain: chainId,
          exclude_spam: true,
          normalizeMetadata: true,
          media_items: true
        }
      });
      data = response.data;
    }

    const nfts: MoralisNFTResponse[] = data?.result || [];
    return nfts
      .filter(n => !n.possible_spam)
      .map(nft => ({
        contractAddress: nft.token_address,
        tokenId: nft.token_id,
        name: nft.normalized_metadata?.name || nft.name || `#${nft.token_id}`,
        description: nft.normalized_metadata?.description || nft.description,
        imageUrl: nft.media?.media_collection?.medium?.url ||
                  nft.normalized_metadata?.image,
        chain,
        collection: {
          name: nft.collection_name || 'Unknown Collection',
          floorPrice: nft.floor_price,
          floorPriceCurrency: nft.floor_price_currency
        },
        estimatedValueUSD: nft.floor_price,
        source: 'moralis' as const
      }));
  } catch (error) {
    console.error(`❌ Moralis getNFTs error for ${chain}:`, error);
    return [];
  }
}

/**
 * Get all NFTs across chains
 */
export async function getAllNFTs(address: string): Promise<NFTItem[]> {
  if (!isMoralisEnabled() && !USE_SERVERLESS_PROXY) {
    return [];
  }

  const chains: Chain[] = ['ethereum', 'polygon', 'bsc', 'arbitrum', 'base'];
  const results = await Promise.allSettled(
    chains.map(chain => getNFTs(address, chain))
  );

  const allNFTs: NFTItem[] = [];
  results.forEach(result => {
    if (result.status === 'fulfilled') {
      allNFTs.push(...result.value);
    }
  });

  return allNFTs;
}

/**
 * Get DeFi positions for a wallet
 */
export async function getDeFiPositions(
  address: string,
  chain: Chain = 'ethereum'
): Promise<DeFiPosition[]> {
  if (!isMoralisEnabled() && !USE_SERVERLESS_PROXY) {
    return [];
  }

  const chainId = MORALIS_CHAIN_IDS[chain];

  try {
    let data: { result?: MoralisDeFiPosition[] };

    if (USE_SERVERLESS_PROXY) {
      data = await callMoralisProxy(`/wallets/${address}/defi/positions`, { chain: chainId });
    } else {
      const client = createMoralisClient();
      const response = await client.get(`/wallets/${address}/defi/positions`, {
        params: { chain: chainId }
      });
      data = response.data;
    }

    const positions: MoralisDeFiPosition[] = data?.result || [];
    return positions.map(pos => ({
      protocolId: pos.protocol_id,
      protocolName: pos.protocol_name,
      protocolLogo: pos.protocol_logo,
      chain,
      type: inferPositionType(pos.position_label || ''),
      suppliedTokens: pos.position.tokens.map(t => convertMoralisToken(t, chain)),
      netValueUSD: pos.position.balance_usd,
      source: 'moralis' as const
    }));
  } catch (error) {
    console.error(`❌ Moralis getDeFiPositions error for ${chain}:`, error);
    return [];
  }
}

/**
 * Infer position type from label
 */
function inferPositionType(label: string): DeFiPosition['type'] {
  const lowerLabel = label.toLowerCase();
  if (lowerLabel.includes('lend') || lowerLabel.includes('supply')) return 'lending';
  if (lowerLabel.includes('stake')) return 'staking';
  if (lowerLabel.includes('farm') || lowerLabel.includes('yield')) return 'farming';
  if (lowerLabel.includes('liquidity') || lowerLabel.includes('lp')) return 'liquidity';
  if (lowerLabel.includes('vest')) return 'vesting';
  return 'other';
}

/**
 * Get all DeFi positions across chains
 */
export async function getAllDeFiPositions(address: string): Promise<DeFiPosition[]> {
  if (!isMoralisEnabled() && !USE_SERVERLESS_PROXY) {
    return [];
  }

  const chains: Chain[] = ['ethereum', 'polygon', 'bsc', 'arbitrum', 'optimism', 'base'];
  const results = await Promise.allSettled(
    chains.map(chain => getDeFiPositions(address, chain))
  );

  const allPositions: DeFiPosition[] = [];
  results.forEach(result => {
    if (result.status === 'fulfilled') {
      allPositions.push(...result.value);
    }
  });

  return allPositions.sort((a, b) => b.netValueUSD - a.netValueUSD);
}

// Export all functions
export const moralisApi = {
  isMoralisEnabled,
  getTokenBalances,
  getAllTokenBalances,
  getNativeBalance,
  getNFTs,
  getAllNFTs,
  getDeFiPositions,
  getAllDeFiPositions
};

export default moralisApi;
